import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
const STORAGE_KEY = "kritaka:theme";
function readStoredMode() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (v === "light" || v === "dark" || v === "system")
            return v;
    }
    catch {
        // localStorage unavailable (SSR or strict-privacy mode) — fall back.
    }
    return "system";
}
function writeStoredMode(mode) {
    try {
        if (mode === "system")
            localStorage.removeItem(STORAGE_KEY);
        else
            localStorage.setItem(STORAGE_KEY, mode);
    }
    catch {
        // Same fallback rationale as readStoredMode — silently best-effort.
    }
}
function applyMode(mode) {
    if (mode === "light" || mode === "dark") {
        document.documentElement.setAttribute("data-theme", mode);
        return;
    }
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    document.documentElement.setAttribute("data-theme", mq.matches ? "light" : "dark");
}
export function ThemeToggle({ style } = {}) {
    const [mode, setMode] = useState(() => readStoredMode());
    // Re-apply when mode changes — and (only when in system mode) hook into
    // OS theme switches so the page recolours live.
    useEffect(() => {
        applyMode(mode);
        if (mode !== "system")
            return;
        const mq = window.matchMedia("(prefers-color-scheme: light)");
        const handler = () => applyMode("system");
        if (mq.addEventListener)
            mq.addEventListener("change", handler);
        else if (mq.addListener)
            mq.addListener(handler);
        return () => {
            if (mq.removeEventListener)
                mq.removeEventListener("change", handler);
            else if (mq.removeListener)
                mq.removeListener(handler);
        };
    }, [mode]);
    const cycle = () => {
        const next = mode === "system" ? "light" : mode === "light" ? "dark" : "system";
        writeStoredMode(next);
        setMode(next);
    };
    const label = mode === "system" ? "System theme" : mode === "light" ? "Light theme" : "Dark theme";
    return (_jsx("button", { type: "button", onClick: cycle, title: `${label} (click to cycle)`, "aria-label": label, style: {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            padding: 0,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "var(--ink-3)",
            borderRadius: 6,
            transition: "color 80ms ease, background 80ms ease",
            ...style,
        }, onMouseEnter: (e) => {
            e.currentTarget.style.color = "var(--ink)";
            e.currentTarget.style.background = "var(--paper-2)";
        }, onMouseLeave: (e) => {
            e.currentTarget.style.color = "var(--ink-3)";
            e.currentTarget.style.background = "transparent";
        }, children: mode === "system" ? _jsx(SystemIcon, {}) : mode === "light" ? _jsx(SunIcon, {}) : _jsx(MoonIcon, {}) }));
}
function SunIcon() {
    return (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("circle", { cx: "12", cy: "12", r: "4" }), _jsx("path", { d: "M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" })] }));
}
function MoonIcon() {
    return (_jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: _jsx("path", { d: "M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" }) }));
}
function SystemIcon() {
    // Monitor glyph — the conventional "follow OS" affordance.
    return (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("rect", { x: "3", y: "4", width: "18", height: "12", rx: "2" }), _jsx("path", { d: "M8 20h8M12 16v4" })] }));
}
//# sourceMappingURL=ThemeToggle.js.map